import { useState } from 'react';
import { line, Color, Size, calendarDayInZone, dealTimingFault, dealTimingHasTime, formatDateOnly, formatDateTime, instantFromWallClock, sameDealTiming, t, wallClockInZone, type DealTimingDates, type StringKey } from '@bms2/shared';
import { Button, Checkbox, DatePicker } from '../../components/controls';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { PAGES_PATH, put } from './api';
import styles from './DealTiming.module.css';

/** The Timing row as the Deal page answers it (decision 0719). */
export interface DealTimingView {
  current: DealTimingDates;
  /** The Organization's zone the server checks the dates in; the row refuses in it too, not in the viewer's (decision 2292). */
  zone?: string;
  /** A note typed on the row before it changed shape, read beneath the dates. */
  typedNote: string | null;
  /** False for a viewer refused write on the Deal module. */
  mayEdit: boolean;
}

/** The dates are written under the Deal, on the record pages' own root, beside the budget. */
export const dealTimingPath = (dealId: string) => `${PAGES_PATH}/deals/${dealId}/timing`;

/** The two dates that take an optional time of day (decision 2292); the Expected quote stays a day. */
const TIMED: ReadonlySet<keyof DealTimingDates> = new Set(['expectedStartDate', 'expectedFinishDate']);

/**
 * 🔒 A timed value as the date-time picker draws it, the wall clock in the
 * viewer's own zone (decision 0261). A day with no time draws at its first
 * minute for the start and its last for the finish, where it stands.
 */
function pickerValue(value: string | null, key: keyof DealTimingDates, zone: string): string {
  if (value === null) return '';
  if (dealTimingHasTime(value)) return wallClockInZone(value, zone);
  return `${value}T${key === 'expectedFinishDate' ? '23:59' : '00:00'}`;
}

/** A start or finish as the row reads it: the day, or the day and the clock in the viewer's zone. */
function readValue(value: string, zone: string, format: ReturnType<typeof useDateFormat>): string {
  return dealTimingHasTime(value) ? formatDateTime(value, zone, format) : formatDateOnly(value, format);
}

/** The three dates in the order the row draws them, each with its own words. */
export const TIMING_FIELDS: readonly { key: keyof DealTimingDates; label: StringKey }[] = [
  { key: 'expectedQuoteDate', label: 'deal.timing.expectedQuote' },
  { key: 'expectedStartDate', label: 'deal.timing.expectedStart' },
  { key: 'expectedFinishDate', label: 'deal.timing.expectedFinish' },
];

/**
 * 🔒 The Intent card's Timing row (decision 0719): Expected quote, Expected start
 * and Expected finish, saved together onto the Deal by the row's own Save.
 * Product owner, 2026-09-24: *"Timing needs to have a few dates: Expected quote,
 * Expected start, Expected finish."*
 *
 * 🔒 The row completes when an Expected quote is saved, so the row draws no
 * checkbox, and the Deal's Close date is its own date (decision 10741). 🔨 An
 * Expected quote after the Expected start, and an Expected finish before it, are
 * each refused by name before anything is sent, and again by the server
 * (decision 5491). 🔨 A viewer who may not edit the Deal reads
 * the dates and is drawn no input.
 *
 * The inputs start from the standing dates; the card keys this block to them, so
 * a save the page reloads after starts the inputs from what was saved.
 */
export function DealTiming({ dealId, timing, editable, apiBaseUrl = '', onChanged, onError }: {
  dealId: string;
  timing: DealTimingView;
  editable: boolean;
  apiBaseUrl?: string;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const format = useDateFormat();
  const zone = useViewerZone();
  const [draft, setDraft] = useState<DealTimingDates>(timing.current);
  // 🔒 The start and the finish take a time only once asked (decision 2292); a row already holding one opens with the times drawn.
  const [withTime, setWithTime] = useState(dealTimingHasTime(timing.current.expectedStartDate) || dealTimingHasTime(timing.current.expectedFinishDate));
  const [saving, setSaving] = useState(false);
  // 🔨 The refusal stands on the date it is about, so the person reads which one to move (decision 5491).
  const fault = dealTimingFault(draft, timing.zone ?? zone);
  const problem = fault?.message ?? null;
  const changed = !sameDealTiming(timing.current, draft);
  const save = async () => {
    if (problem) return;
    setSaving(true);
    const answer = await put(dealTimingPath(dealId), draft, apiBaseUrl);
    setSaving(false);
    if (!answer.ok) return onError(answer.message);
    onChanged();
  };
  const canEdit = editable && timing.mayEdit;
  const set = (key: keyof DealTimingDates) => (value: string) => setDraft((held) => ({ ...held, [key]: value || null }));
  // 🔒 A clock picked in the viewer's zone is sent as the instant it names, through the shared helper and never inline (decision 0261).
  const setTimed = (key: keyof DealTimingDates) => (value: string) => setDraft((held) => ({ ...held, [key]: value ? instantFromWallClock(value, zone).toISOString() : null }));
  // Turning the times off keeps each date's day as the viewer reads it and drops its clock.
  const toggleTime = (next: boolean) => {
    setWithTime(next);
    if (!next) {
      setDraft((held) => {
        const day = (value: string | null) => (value !== null && dealTimingHasTime(value) ? calendarDayInZone(value, zone) : value);
        return { ...held, expectedStartDate: day(held.expectedStartDate), expectedFinishDate: day(held.expectedFinishDate) };
      });
    }
  };
  return (
    <div className={styles.block} data-deal-timing={canEdit ? 'edit' : 'read'}>
      {canEdit ? (
        <div className={styles.inputs}>
          {TIMING_FIELDS.map((field) => {
            const timed = withTime && TIMED.has(field.key);
            return (
              <DatePicker
                key={field.key}
                size={timed ? Size.Medium : Size.Small}
                label={t(field.label)}
                value={timed ? pickerValue(draft[field.key], field.key, zone) : draft[field.key]?.slice(0, 10) ?? ''}
                onChange={timed ? setTimed(field.key) : set(field.key)}
                mode={timed ? 'datetime' : 'date'}
                name={`timing-${field.key}`}
                error={fault !== null && field.key === fault.key ? fault.message : null}
              />
            );
          })}
          <span className={styles.withTime} data-deal-timing-with-time={String(withTime)}>
            <Checkbox size={Size.Small} label={t('deal.timing.withTime')} checked={withTime} onChange={toggleTime} name="timing-with-time" />
          </span>
          <span className={styles.save} data-deal-timing-action="save">
            <Button size={Size.Small} color={Color.Navy} text={[t('deal.timing.save')]} actions={[() => void save()]} disabled={saving || !changed || problem !== null} />
          </span>
        </div>
      ) : (
        <p className={styles.standing} data-deal-timing-standing="true">
          {TIMING_FIELDS.some((field) => timing.current[field.key] !== null)
            ? TIMING_FIELDS.filter((field) => timing.current[field.key] !== null).map((field) => line(t(field.label), readValue(timing.current[field.key]!, zone, format))).join(' · ')
            : t('deal.timing.none')}
        </p>
      )}
      {timing.typedNote ? (
        <div className={styles.typed} data-deal-timing-typed-note="true">
          <p className={styles.label}>{t('deal.timing.earlierNote')}</p>
          <p className={styles.note}>{timing.typedNote}</p>
        </div>
      ) : null}
    </div>
  );
}
